import { useCallback } from 'react';

import useConfirm from '@/hooks/useConfirm';

import { clampActivityExportDateRange, isActivityExportRangeWiderThanMax } from '../utils/activityExportRange';

export interface ActivityExportDates {
  from?: string;
  to?: string;
}

const EXPORT_LIMITED_TITLE = 'Export limited to the last 2 weeks';
const EXPORT_LIMITED_MESSAGE =
  "Your selected date range is longer than 2 weeks. Only the last 2 weeks of data will be included in this export. If you need a longer range, contact us and we'll provide it manually.";

export function useActivityExportConfirmation() {
  const [ConfirmationDialog, confirm] = useConfirm('warning');

  const requestExport = useCallback(
    async ({
      from,
      to,
      onExport,
    }: {
      from?: string | null;
      to?: string | null;
      onExport: (dates: ActivityExportDates) => void;
    }) => {
      if (!from || !to) {
        onExport({ from: from ?? undefined, to: to ?? undefined });
        return;
      }

      if (!isActivityExportRangeWiderThanMax(from, to)) {
        onExport({ from, to });
        return;
      }

      const confirmed = await confirm({
        title: EXPORT_LIMITED_TITLE,
        message: EXPORT_LIMITED_MESSAGE,
        confirmButtonText: 'Export last 2 weeks',
        cancelButtonText: 'Cancel',
      });

      if (!confirmed) {
        return;
      }

      onExport(clampActivityExportDateRange(from, to));
    },
    [confirm],
  );

  return { ConfirmationDialog, requestExport };
}
